<template>
  <div class="i-report-navbar">
    <div class="wrap">
      <div class="logo">
        <img
          :src="logo"
          loading="lazy"
          alt="Jobtest Logo"
          @click="redirectToHome"
        />
      </div>

      <div
        v-if="isDesktop && userStore.isReportUser && userStore.isTierFreeOrHigher"
        class="core-items"
      >
        <iOnboardingTooltip :value="1" />
        <template
          v-for="item in coreItems.filter((e) => !e.isHidden)"
          :key="item.text"
        >
          <iNavbarItem
            :text="item.text"
            :is-active="item.isActive"
            @click="item.action"
          />
        </template>
      </div>

      <iNavbarDropdown
        :dropdown-items="{
          support: props.supportItems,
          [isSmOrLarger ? 'profile' : userStore.getFullName || 'Your Details']: props.profileItems
        }"
        :dropdown-margin-top="dropdownMarginTop"
        :screen-width="navbarWidth"
        align="right"
      >
        <iNavbarItem
          :is-dropdown-item="true"
          :text="isSmOrLarger ? userStore.getFullName || 'Your Details' : ''"
          :use-more-icon="!isSmOrLarger"
        >
          <template
            v-if="isSmOrLarger"
            #icon
          >
            <span class="user-initials icon">{{ userStore.getUserInitials }}</span>
          </template>
        </iNavbarItem>
      </iNavbarDropdown>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue"
import { useRouter } from "vue-router"

import useIsScreenSize from "@/composables/useIsScreenSize"
import useComponentWidth from "@/composables/useComponentWidth"
import { GlobalVariables } from "@/helpers/globalVariables"
import { useUserStore } from "@/stores/user"

// Components
import iNavbarDropdown from "@/components/report/navbar/NavbarDropdown.vue"
import iNavbarItem from "@/components/report/navbar/NavbarItem.vue"
import iOnboardingTooltip from "@/components/report/OnboardingTooltip.vue"

// Assets
import logo from "@/assets/logo/logo-dark.svg"

// Styles
import { breakpointLg, breakpointSm } from "@/styles/variables"

// Types
import type { NavigationItem } from "@/types/navigationItem"
import type { PropType } from "vue"

// Props
const props = defineProps({
  coreItems: {
    type: Array as PropType<NavigationItem[]>,
    default: () => []
  },
  profileItems: {
    type: Array as PropType<NavigationItem[]>,
    default: () => []
  },
  supportItems: {
    type: Array as PropType<NavigationItem[]>,
    default: () => []
  }
})

// Stores
const userStore = useUserStore()
const router = useRouter()

// Composables
const isDesktop = useIsScreenSize(breakpointLg)
const isSmOrLarger = useIsScreenSize(breakpointSm)
const navbarWidth = useComponentWidth()

// COMPUTED
const dropdownMarginTop = computed(() => (isDesktop.value ? 24 : 8))

// FUNCTIONS
const redirectToHome = () => {
  router.push({ name: GlobalVariables.urls.report.children.home.routeName })
}
</script>

<style lang="scss" scoped>
.i-report-navbar {
  z-index: $z-index-navbar;
  width: 100%;
  background-color: $white;
  box-shadow: 0 5px 10px 0 rgb(83 83 83 / 7%);

  @media screen and (min-width: $breakpoint-lg) {
    top: 0;
    position: sticky;
  }

  .wrap {
    background-color: $white;
    width: 100%;
    padding: 24px;
    height: 48px;

    @include flex($direction: row, $justify: space-between, $align: center, $gap: 8px);

    @media screen and (min-width: $breakpoint-lg) {
      height: 80px;
    }

    .logo {
      cursor: pointer;
      padding: 4px;

      img {
        width: 121px;
        height: 28.8px;
      }
    }

    .core-items {
      @include flex($direction: row, $justify: center, $align: center, $gap: 24px);

      @media screen and (min-width: $breakpoint-xl) {
        gap: 43px;
      }
    }

    .user-initials {
      font-size: 10px;
      font-weight: 700;
      line-height: 16px;
      color: $pickle-bluewood900;
      background-color: $brand-opacity100;
      border-radius: 100%;
      height: 100%;
      width: 100%;
      text-transform: uppercase;

      @include flex(row, center, center);
    }

    .icon {
      width: 24px;
      height: 24px;
    }
  }
}

@media print {
  .i-report-navbar {
    display: none;
  }
}
</style>
